/* eslint functional/prefer-tacit: "error" */
import { useEffect, useState } from 'react';
import { getIn, useFormikContext } from 'formik';
import hash from 'object-hash';

import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import Switch from '@mui/material/Switch';

import { ActorCategoryEnum, ActorTypeEnum } from '@e-socna/data-layer';
import type { Id } from '@e-socna/data-layer';

import { CheckboxList } from '../../../../components/CheckboxList';
import { NoticesAlert } from '../../../../components/NoticesAlert';
import { useReferencesContext } from '../../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useFormikOnNextTick } from '../../../../hooks/useFormikOnNextTick.hook';

import { UserRolesFormControl } from '../../../UserRoles';

import type { ActorForm_Actor } from '../../fragments/actor.fragment';
import type { CategoryRestriction } from '../../types/category-restriction.type';

interface Props {
  readonly allowsInternalQuotationReceiver: boolean;
  readonly categoryRestriction?: CategoryRestriction;
  readonly id: string;
  // The internal list, the only one carrying the EDM domain and never the quotation receiver.
  readonly isInternalList: boolean;
}

export const ActorDetailsFormStep = ({
  allowsInternalQuotationReceiver,
  categoryRestriction,
  isInternalList,
}: Readonly<Props>) => {
  const { errors, handleBlur, handleChange, setFieldTouched, touched, values } =
    useFormikContext<ActorForm_Actor>();

  const { setFieldValue: setFieldValueOnNextTick } = useFormikOnNextTick();

  const checkboxIdPrefix = `${hash(`${values._id}-categoryIds`)}-`;

  const [checkedCategoryIds, setCheckedCategoryIds] = useState(
    values.categoryIds,
  );

  useEffect(() => {
    setFieldValueOnNextTick('categoryIds', checkedCategoryIds);
  }, [checkedCategoryIds, setFieldValueOnNextTick]);

  const { actorCategories } = useReferencesContext();

  // An organization of the group is the quotation receiver only in the external list (a recharged
  // internal), as the back requires.
  const relevantCategories = actorCategories.filter((category) =>
    values.isInternal
      ? category.isInternal ||
        (allowsInternalQuotationReceiver &&
          !isInternalList &&
          category.code === ActorCategoryEnum.QuotationReceiver)
      : category.isExternal,
  );

  // A category the third party cannot hold stays uncheckable, unless it already holds it.
  const restrictedCategoryId =
    categoryRestriction &&
    !categoryRestriction.holderIds.includes(values.thirdPartyId) &&
    !checkedCategoryIds.includes(categoryRestriction.categoryId)
      ? categoryRestriction.categoryId
      : undefined;

  return (
    <Grid container spacing={2} sx={{ alignContent: 'flex-start' }}>
      {categoryRestriction && restrictedCategoryId ? (
        <Grid size={12} sx={{ pt: 1 }}>
          <NoticesAlert notices={[categoryRestriction.notice]} />
        </Grid>
      ) : null}
      <Grid size={12} sx={{ pt: 1, pb: 1 }}>
        <FormControl
          component="fieldset"
          error={
            !!(getIn(errors, 'categoryIds') && getIn(touched, 'categoryIds'))
          }
          required
          sx={{ m: 3 }}
          variant="standard"
        >
          <FormLabel component="legend">
            Choisissez les catégories de l&apos;acteur
          </FormLabel>
          <CheckboxList<Id>
            idPrefix={checkboxIdPrefix}
            items={relevantCategories.map((category) => ({
              id: category._id,
              label: category.label,
              disabled: category._id === restrictedCategoryId,
            }))}
            // eslint-disable-next-line react/jsx-no-bind
            onChange={(categoryIds) => {
              setCheckedCategoryIds(categoryIds);
              void setFieldTouched('categoryIds', true, false);
            }}
            value={checkedCategoryIds}
            variant="edition"
          />
          {!!(
            getIn(errors, 'categoryIds') && getIn(touched, 'categoryIds')
          ) && <FormHelperText>{getIn(errors, 'categoryIds')}</FormHelperText>}
        </FormControl>
      </Grid>
      <Grid size={12} sx={{ pt: 1, pb: 1 }}>
        {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
        {!!(
          values.__typename === ActorTypeEnum.ActorPerson && // eslint-disable-line @typescript-eslint/no-unsafe-enum-comparison
          !values.isInternal
        ) && (
          <UserRolesFormControl
            fallbackRoleIds={undefined}
            name="contextualRoleIds"
            required={false}
            scope="contextual"
          />
        )}
        {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
        {!!(
          values.__typename === ActorTypeEnum.ActorOrganization && // eslint-disable-line @typescript-eslint/no-unsafe-enum-comparison
          isInternalList
        ) && (
          <FormControl
            error={
              !!(
                getIn(errors, 'useItsEdmDomain') &&
                getIn(touched, 'useItsEdmDomain')
              )
            }
            sx={{ m: 3 }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={!!values.useItsEdmDomain}
                  id="actor-details-useItsEdmDomain"
                  name="useItsEdmDomain"
                  onBlur={handleBlur}
                  onChange={handleChange}
                />
              }
              label="Utiliser son domaine GED"
            />
            {!!(
              getIn(errors, 'useItsEdmDomain') &&
              getIn(touched, 'useItsEdmDomain')
            ) && (
              <FormHelperText>
                {getIn(errors, 'useItsEdmDomain')}
              </FormHelperText>
            )}
          </FormControl>
        )}
      </Grid>
    </Grid>
  );
};
